import type { Coding, Parameters } from '@medplum/fhirtypes'
import { match, P } from 'ts-pattern'
import type { SeenMedplumClient } from '#src/client.ts'

export type ScaleType = 'Qn' | 'Ord' | 'Nom' | 'Nar'

export async function getCodingDetails(
  medplum: SeenMedplumClient,
  coding: Coding
): Promise<{
  scaleType: ScaleType
  unit: string | null
  answerOptions: { code: string; display: string }[]
  display: string
}> {
  const { code, system } = coding
  if (!code || !system) {
    return {
      scaleType: 'Qn',
      unit: null,
      answerOptions: [],
      display: '',
    }
  }

  let scaleType: ScaleType = 'Qn'
  let unit: string | null = null
  const answerOptions: { code: string; display: string }[] = []

  const codeSystem = await medplum.get<Parameters>(
    `/fhir/R4/CodeSystem/$lookup?system=${system}&code=${code}`
  )

  const display =
    codeSystem?.parameter?.find((p) => p.name === 'display')?.valueString ?? ''

  if (codeSystem?.parameter) {
    const properties = codeSystem.parameter.filter((p) => p.name === 'property')

    for (const prop of properties) {
      const propCode = prop.part?.find((p) => p.name === 'code')?.valueCode
      const propValue = prop.part?.find((p) => p.name === 'value')?.valueString

      if (propCode === 'SCALE_TYP' && propValue) {
        scaleType = propValue as ScaleType
      }

      // Most accurate unit source: EXAMPLE_UCUM_UNITS
      if (propCode === 'EXAMPLE_UCUM_UNITS' && propValue) {
        unit = overrideExampleUnitMapping(propValue)
      }

      // Optional fallback inference if EXAMPLE_UCUM_UNITS is missing
      if (!unit && propCode === 'PROPERTY' && propValue) {
        unit = inferUnitFromProperty(propValue)
      }

      // Answer options
      if (propCode === 'answer_to') {
        const answerCode = prop.part?.find((p) => p.name === 'value')?.valueCode
        const answerDisplay = prop.part?.find(
          (p) => p.name === 'description'
        )?.valueString
        if (answerCode && answerDisplay) {
          answerOptions.push({ code: answerCode, display: answerDisplay })
        }
      }
    }
  }

  return {
    scaleType,
    unit,
    answerOptions,
    display,
  }
}

// Unified Code for Units of Measure (UCUM) https://ucum.org/ucum
function overrideExampleUnitMapping(unit: string): string {
  return (
    match(unit)
      .with('Cel', () => '[degF]')
      .with(
        P.union(P.string.includes('in_us'), P.string.includes('lb_av')),
        () => (unit.includes('in_us') ? '[in_us]' : '[lb_av]')
      )
      // Example units may show multiple units delimited by a semicolon. In these cases, we only want to show the first unit.
      .with(P.string.includes(';'), () => unit.split(';')[0])
      .otherwise(() => unit)
  )
}

function inferUnitFromProperty(property: string): string | null {
  interface UnitsByProperty {
    [property: string]: string
  }

  const unitMap: UnitsByProperty = {
    MCnc: 'mg/dL',
    SCnc: 'mmol/L',
    Temp: '°F',
    Pres: 'mmHg',
    Len: 'inches',
    Mass: 'lbs',
    Vol: 'mL',
    Time: 's',
    Freq: 'Hz',
    Vel: 'm/s',
    Accel: 'm/s²',
    Force: 'N',
    Energy: 'J',
    Power: 'W',
  }

  for (const [key, unit] of Object.entries(unitMap)) {
    if (property.includes(key)) {
      return unit
    }
  }

  return null
}
